<template>
  <div id="q-app">
    <LoadingIndicator />

    <transition
      enter-active-class="animated fadeIn"
      leave-active-class="animated fadeOut"
      appear
      :duration="300"
    >
      <router-view v-if="!isBooting" />
    </transition>
  </div>
</template>
<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import errorDialog from 'quasar-pilot-core/src/mixins/errorDialog';
import LoadingIndicator from 'quasar-pilot-core/src/components/ui/LoadingIndicator';
import LocationModel from 'quasar-pilot-core/src/lib/models/location';
import constants from './lib/constants';

export default {
  name: 'App',
  components: { LoadingIndicator },
  mixins: [errorDialog],

  data() {
    return {
      waitUpdateInterval: null,
    };
  },

  computed: {
    ...mapState('application', ['isBooting', 'closed']),
    ...mapState('location', { locations: 'all', currentLocation: 'current' }),
    ...mapState('user', { availableUsers: 'all', isLoggedIn: 'isLoggedIn' }),
    ...mapGetters('location', ['findLocation', 'findLocationByUUID']),
    ...mapGetters('user', ['findUserByUUID']),
  },

  watch: {
    // eslint-disable-next-line func-names
    '$q.appVisible': async function (visible) {
      // Checks if app is visible or in background
      if (visible) {
        try {
          await this.boot();
        }
        catch (e) {
          this.showErrorDialog(e);
        }

        await this.initUser();
        await this.initLocation();
        this.startWaitUpdates();
      }
      else {
        this.stopWaitUpdates();
      }
    },
  },

  async mounted() {
    try {
      await this.boot();
    }
    catch (e) {
      this.showErrorDialog(e);
    }
    finally {
      await this.initLocation();
      await this.initUser();
      this.startWaitUpdates();
      this.bootComplete();

      // Replace current route with the home route.
      // This should remove uuid's from url when scanning qr.
      // Also should only happen on app opening.
      await this.$router.replace({ name: 'Home' }).catch(() => {});
    }
  },

  created() {
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
    });
  },

  methods: {
    ...mapActions('category', ['fetchCategories']),
    ...mapActions('location', ['fetchLocations', 'fetchDeliveryMethods']),
    ...mapActions('payment', ['fetchAllPaymentMethods']),
    ...mapActions('user', ['setUserAccessToken', 'fetchUserByUUID']),
    ...mapMutations('user', ['updateUser']),
    ...mapMutations('location', ['updateCurrentLocation']),
    ...mapMutations('application', [
      'bootComplete',
      'setAsQR',
      'updateDefaultDeliveryMethod',
    ]),

    async boot() {
      await this.fetchDeliveryMethods();
      await this.fetchAllPaymentMethods();
      await this.fetchLocations();
      await this.fetchCategories();
    },

    async initLocation() {
      let location;
      // Load location based on uuid in parameter
      if (this.$route.query.l) {
        // Store the uuid for 15 minutes
        this.$q.cookies.set(
          constants.SESSION_COOKIE_LOCATION_ID,
          this.$route.query.l,
          {
            expires: '15m',
          },
        );

        location = new LocationModel(this.findLocationByUUID(this.$route.query.l));
        this.setAsQR();
      }

      // Initialize location from cookie if it exists
      else if (this.$q.cookies.has(constants.SESSION_COOKIE_LOCATION_ID)) {
        location = new LocationModel(this.findLocationByUUID(
          this.$q.cookies.get(constants.SESSION_COOKIE_LOCATION_ID),
        ));
        this.setAsQR();
      }

      if (!location) {
        location = new LocationModel(this.findLocation(constants.DEFAULT_LOCATION));
      }

      await this.updateCurrentLocation(location);

      // Set the default delivery method based on the available methods on the location
      if (location.deliveryMethods && location.deliveryMethods.length > 0) {
        await this.updateDefaultDeliveryMethod({ deliveryMethod: location.deliveryMethods[0] });
      }
    },

    async initUser() {
      let user;
      // Load user based on uuid in parameter
      if (this.$route.query.u) {
        // Store the uuid for 15 minutes
        this.$q.cookies.set(
          constants.SESSION_COOKIE_USER_ID,
          this.$route.query.u,
          {
            expires: '15m',
          },
        );

        user = this.fetchUserByUUID({ uuid: this.$route.query.u });
      }

      // Initialize user from cookie if it exists
      else if (this.$q.cookies.has(constants.SESSION_COOKIE_USER_ID)) {
        user = this.fetchUserByUUID({
          uuid: this.$q.cookies.get(constants.SESSION_COOKIE_USER_ID),
        });
      }

      if (user) {
        await this.updateUser(user);
        await this.setUserAccessToken(user.accessToken);
        this.setAsQR();
      }
    },

    startWaitUpdates() {
      if (!this.waitUpdateInterval) {
        this.waitUpdateInterval = setInterval(() => {
          // Keep updating waiting times
          this.fetchLocations();
        }, 600000); // 10 minutes
      }
    },

    stopWaitUpdates() {
      if (this.waitUpdateInterval) {
        clearInterval(this.waitUpdateInterval);
        this.waitUpdateInterval = null;
      }
    },
  },
};
</script>
